import { useState } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import { toast } from 'sonner';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { ProtectedEmail } from '@/components/ProtectedContact';
import PageSEO from '@/components/PageSEO';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Calendar } from '@/components/ui/calendar';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { format } from 'date-fns';
import { it } from 'date-fns/locale';
import { 
  CalendarIcon, 
  Clock, 
  Video, 
  Euro,
  Loader2,
  CreditCard,
  ShieldCheck
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const formSchema = z.object({
  nome: z.string().min(2, 'Nome richiesto'),
  cognome: z.string().min(2, 'Cognome richiesto'),
  email: z.string().email('Email non valida'),
  telefono: z.string().min(8, 'Telefono richiesto'),
  tipo_consulenza: z.string().min(1, 'Seleziona il tipo di consulenza'),
  messaggio: z.string().optional(),
  privacy: z.boolean().refine(val => val === true, 'Devi accettare la privacy policy')
});

const ConsulenzaPage = () => {
  const [date, setDate] = useState(null);
  const { t } = useTranslation();
  const [time, setTime] = useState('');
  const [loading, setLoading] = useState(false);

  const { register, handleSubmit, formState: { errors }, setValue, watch } = useForm({
    resolver: zodResolver(formSchema),
    defaultValues: { privacy: false }
  });

  const tipiConsulenza = [
    { value: 'startup', label: 'Startup', price: 150, desc: 'Sciogli tutti i dubbi legali e fiscali per l\'avvio della tua start-up in qualsiasi settore' },
    { value: 'ecommerce', label: 'E-commerce', price: 200, desc: 'Specifico su tematiche contabili, fiscali o previdenziali inerenti all\'e-commerce' },
    { value: 'business-model-test', label: 'Business Model Test', price: 200, desc: 'Analizzare le debolezze e le criticità; proporre soluzioni solo per attività legate ad Internet' },
  ];

  const orariDisponibili = [
    '09:00', '10:00', '11:00', '14:00', '15:00', '16:00', '17:00'
  ];

  const selectedTipo = watch('tipo_consulenza');
  const selectedPrice = tipiConsulenza.find(t => t.value === selectedTipo)?.price || 0;

  const onSubmit = async (data) => {
    if (!date || !time) {
      toast.error('Seleziona data e ora della consulenza');
      return;
    }

    setLoading(true);

    try {
      // Step 1: Create consultation record
      const consultResponse = await axios.post(`${API}/consultations`, {
        ...data,
        data_preferita: format(date, 'yyyy-MM-dd'),
        ora_preferita: time
      });

      const consultationId = consultResponse.data.id;

      // Step 2: Create Stripe checkout session
      const checkoutResponse = await axios.post(`${API}/create-checkout-session`, {
        consultation_id: consultationId,
        origin_url: window.location.origin
      });

      // Step 3: Redirect to Stripe checkout
      if (checkoutResponse.data.url) {
        toast.success('Reindirizzamento al pagamento...');
        window.location.href = checkoutResponse.data.url;
      } else {
        throw new Error('Nessun URL di pagamento ricevuto');
      }
    } catch (error) {
      console.error('Error:', error);
      const message = error.response?.data?.detail || 'Errore durante la prenotazione. Riprova.';
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen" data-testid="consulenza-page">
      <PageSEO path="/consulenza" />
      {/* Hero */}
      <section className="bg-white py-16 lg:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center max-w-3xl mx-auto mb-16"
          >
            <span className="inline-flex items-center gap-2 px-4 py-2 bg-indigo-50 text-indigo-700 rounded-full text-sm font-medium mb-6">
              <Video className="w-4 h-4" />
              ilmiocommercialista.online
            </span>
            <h1 className="text-4xl md:text-5xl font-bold text-slate-900 mb-6 tracking-tight">
              {t('consulenza.title')}
            </h1>
            <p className="text-lg text-slate-600">
              {t('consulenza.subtitle')}
            </p>
          </motion.div>

          <div className="grid lg:grid-cols-3 gap-8">
            {/* Form */}
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.2 }}
              className="lg:col-span-2"
            >
              <form onSubmit={handleSubmit(onSubmit)} className="bg-white rounded-2xl border border-slate-200 p-8">
                <h2 className="text-2xl font-bold text-slate-900 mb-6">
                  Dati per la prenotazione
                </h2>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                  <div>
                    <Label htmlFor="nome">Nome *</Label>
                    <Input
                      id="nome"
                      {...register('nome')}
                      placeholder="Mario"
                      className="mt-2"
                      data-testid="input-nome"
                    />
                    {errors.nome && (
                      <p className="text-red-500 text-sm mt-1">{errors.nome.message}</p>
                    )}
                  </div>
                  <div>
                    <Label htmlFor="cognome">Cognome *</Label>
                    <Input
                      id="cognome"
                      {...register('cognome')}
                      placeholder="Rossi"
                      className="mt-2"
                      data-testid="input-cognome"
                    />
                    {errors.cognome && (
                      <p className="text-red-500 text-sm mt-1">{errors.cognome.message}</p>
                    )}
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                  <div>
                    <Label htmlFor="email">Email *</Label>
                    <Input
                      id="email"
                      type="email"
                      {...register('email')}
                      placeholder="mario.rossi@email.com"
                      className="mt-2"
                      data-testid="input-email"
                    />
                    {errors.email && (
                      <p className="text-red-500 text-sm mt-1">{errors.email.message}</p>
                    )}
                  </div>
                  <div>
                    <Label htmlFor="telefono">Telefono *</Label>
                    <Input
                      id="telefono"
                      {...register('telefono')}
                      placeholder="+39 333 1234567"
                      className="mt-2"
                      data-testid="input-telefono"
                    />
                    {errors.telefono && (
                      <p className="text-red-500 text-sm mt-1">{errors.telefono.message}</p>
                    )}
                  </div>
                </div>

                <div className="mb-6">
                  <Label>Tipo di consulenza *</Label>
                  <Select onValueChange={(value) => setValue('tipo_consulenza', value)}>
                    <SelectTrigger className="mt-2" data-testid="select-tipo">
                      <SelectValue placeholder="Seleziona il tipo di consulenza" />
                    </SelectTrigger>
                    <SelectContent>
                      {tipiConsulenza.map((tipo) => (
                        <SelectItem key={tipo.value} value={tipo.value}>
                          {tipo.label} - €{tipo.price}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {errors.tipo_consulenza && (
                    <p className="text-red-500 text-sm mt-1">{errors.tipo_consulenza.message}</p>
                  )}
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                  <div>
                    <Label>Data preferita *</Label>
                    <Popover>
                      <PopoverTrigger asChild>
                        <Button
                          variant="outline"
                          className="w-full mt-2 justify-start text-left font-normal"
                          data-testid="date-picker"
                        >
                          <CalendarIcon className="mr-2 h-4 w-4" />
                          {date ? format(date, 'PPP', { locale: it }) : 'Seleziona una data'}
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-auto p-0" align="start">
                        <Calendar
                          mode="single"
                          selected={date}
                          onSelect={setDate}
                          disabled={(date) => date < new Date() || date.getDay() === 0 || date.getDay() === 6}
                          initialFocus
                        />
                      </PopoverContent>
                    </Popover>
                  </div>
                  <div>
                    <Label>Ora preferita *</Label>
                    <Select onValueChange={setTime}>
                      <SelectTrigger className="mt-2" data-testid="select-ora">
                        <SelectValue placeholder="Seleziona l'orario" />
                      </SelectTrigger>
                      <SelectContent>
                        {orariDisponibili.map((ora) => (
                          <SelectItem key={ora} value={ora}>
                            <div className="flex items-center gap-2">
                              <Clock className="w-4 h-4" />
                              {ora}
                            </div>
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div className="mb-6">
                  <Label htmlFor="messaggio">Messaggio (opzionale)</Label>
                  <Textarea
                    id="messaggio"
                    {...register('messaggio')}
                    placeholder="Descrivi brevemente l'argomento della consulenza..."
                    className="mt-2 min-h-[120px]"
                    data-testid="input-messaggio"
                  />
                </div>

                {/* Privacy Consent */}
                <div className="mb-6 p-4 bg-slate-50 rounded-lg">
                  <div className="flex items-start gap-3">
                    <Checkbox 
                      id="privacy"
                      checked={watch('privacy')}
                      onCheckedChange={(checked) => setValue('privacy', checked)}
                      data-testid="privacy-checkbox"
                    />
                    <div className="text-sm">
                      <Label htmlFor="privacy" className="font-normal text-slate-600 cursor-pointer">
                        Ho letto e accetto la{' '}
                        <Link to="/privacy-policy" className="text-indigo-600 hover:underline" target="_blank">
                          Privacy Policy
                        </Link>{' '}
                        e acconsento al trattamento dei miei dati personali per la gestione della richiesta di consulenza. *
                      </Label>
                      {errors.privacy && (
                        <p className="text-red-500 text-sm mt-1">{errors.privacy.message}</p>
                      )}
                    </div>
                  </div>
                </div>

                {/* Payment info banner */}
                <div className="mb-6 p-4 bg-indigo-50 rounded-lg border border-indigo-100">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-indigo-100 rounded-full flex items-center justify-center flex-shrink-0">
                      <CreditCard className="w-5 h-5 text-indigo-600" />
                    </div>
                    <div>
                      <p className="text-sm font-medium text-slate-900">Pagamento sicuro con Stripe</p>
                      <p className="text-xs text-slate-500">
                        Dopo l'invio verrai reindirizzato alla pagina di pagamento sicuro
                      </p>
                    </div>
                    <ShieldCheck className="w-5 h-5 text-green-600 ml-auto flex-shrink-0" />
                  </div>
                </div>

                <Button 
                  type="submit" 
                  size="lg" 
                  className="w-full btn-accent"
                  disabled={loading}
                  data-testid="submit-consultation"
                >
                  {loading ? (
                    <>
                      <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                      Elaborazione in corso...
                    </>
                  ) : (
                    <>
                      <CreditCard className="w-5 h-5 mr-2" />
                      Prenota e Paga
                      {selectedPrice > 0 && ` - €${selectedPrice}`}
                    </>
                  )}
                </Button>
              </form>
            </motion.div>

            {/* Sidebar */}
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.3 }}
              className="space-y-6"
            >
              {/* How it works */}
              <div className="bg-slate-50 rounded-2xl p-6 border border-slate-200">
                <h3 className="text-lg font-semibold text-slate-900 mb-4">
                  Come funziona
                </h3>
                <ul className="space-y-4">
                  {[
                    'Compila il form con i tuoi dati',
                    'Scegli data e ora disponibili',
                    'Effettua il pagamento online',
                    'Ricevi conferma via email con link video'
                  ].map((step, index) => (
                    <li key={index} className="flex items-start gap-3">
                      <div className="w-6 h-6 bg-indigo-100 rounded-full flex items-center justify-center flex-shrink-0 text-sm font-medium text-indigo-600">
                        {index + 1}
                      </div>
                      <span className="text-slate-600 text-sm">{step}</span>
                    </li>
                  ))}
                </ul>
              </div>

              {/* Pricing Card */}
              <div className="bg-indigo-600 rounded-2xl p-6 text-white">
                <div className="flex items-center gap-2 mb-4">
                  <Euro className="w-5 h-5" />
                  <h3 className="text-lg font-semibold">Tariffe</h3>
                </div>
                <ul className="space-y-4">
                  {tipiConsulenza.map((tipo) => (
                    <li key={tipo.value} className="border-b border-indigo-500/30 last:border-0 pb-3 last:pb-0">
                      <div className="flex items-center justify-between text-sm mb-1">
                        <span className="font-semibold text-white">{tipo.label}</span>
                        <span className="font-bold text-lg">€{tipo.price}</span>
                      </div>
                      <p className="text-xs text-indigo-200">{tipo.desc}</p>
                    </li>
                  ))}
                </ul>
                <p className="text-xs text-indigo-200 mt-4">
                  * Colloquio via videoconferenza - Durata: 60 minuti
                </p>
              </div>

              {/* Secure payment */}
              <div className="bg-white rounded-2xl p-6 border border-slate-200">
                <div className="flex items-center gap-2 mb-3">
                  <ShieldCheck className="w-5 h-5 text-green-600" />
                  <h3 className="text-lg font-semibold text-slate-900">Pagamento sicuro</h3>
                </div>
                <p className="text-slate-600 text-sm mb-3">
                  I pagamenti sono gestiti in modo sicuro tramite Stripe. 
                  I tuoi dati di pagamento non vengono mai memorizzati sui nostri server.
                </p>
                <div className="flex items-center gap-2 text-xs text-slate-400">
                  <CreditCard className="w-4 h-4" />
                  <span>Visa, Mastercard, American Express</span>
                </div>
              </div>

              {/* Contact Card */}
              <div className="bg-white rounded-2xl p-6 border border-slate-200">
                <h3 className="text-lg font-semibold text-slate-900 mb-2">
                  Hai domande?
                </h3>
                <p className="text-slate-600 text-sm mb-4">
                  Usa il chatbot in basso a destra per domande rapide o contattaci direttamente.
                </p>
                <p className="text-sm">
                  <span className="text-slate-500">Email:</span>{' '}
                  <ProtectedEmail user="info" domain="studioazzanostp.it" className="text-indigo-600 hover:underline" />
                </p>
              </div>
            </motion.div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ConsulenzaPage;
